<template>
  <div class="promo-usage-container">
    <div v-if="localGroupId">
      <!-- Table -->
      <div v-if="isLoading" class="text-center py-8">
        <div class="loading mx-auto"></div>
      </div>
      <div v-else>
        <hs-table
          :headers="headers"
          :data="tableRows"
          :headerKeyMapping="headerKeyMapping"
          :rowClickable="true"
          @row-click="viewDetails"
        >
          <template #cell-sale_id="{ value }">
            <span class="font-medium">#{{ value }}</span>
          </template>
          <template #cell-store="{ value }">
            <span class="font-medium">{{ value }}</span>
          </template>
          <template #cell-promo_code="{ value }">
            <span
              class="px-2 rounded bg-blue-100 text-blue-700 dark:bg-blue-600 dark:text-white font-semibold text-sm"
            >
              {{ value }}
            </span>
          </template>
          <template #cell-discount="{ value }">
            <span class="text-success font-bold">-{{ value }}</span>
          </template>
          <template #cell-actions="{ row }">
            <button
              @click.stop="viewDetails(row.__raw)"
              class="px-3 text-xs font-medium rounded bg-primary hover:bg-primary-dark text-white transition-colors"
            >
              View
            </button>
          </template>
        </hs-table>

        <hs-pagination
          class="mt-4"
          :currentPage="currentPage"
          :totalPages="totalPages"
          :totalItems="totalItems"
          :itemsPerPage="perPage"
          @previous-page="goToPage(currentPage - 1)"
          @next-page="goToPage(currentPage + 1)"
          @update:itemsPerPage="updatePerPage"
        />
      </div>
    </div>

    <!-- Modals -->
    <SaleCartDetailsModal
      v-if="selectedSaleCart"
      :sale-cart="selectedSaleCart"
      :store-id="storeId"
      @close="selectedSaleCart = null"
      @sale-cart-updated="handleSaleCartUpdated"
    />
  </div>
</template>

<script>
import { ref, computed, watch } from "vue";
import { toast } from "vue3-toastify";
import {
  useGroupPromoUsage,
  useStoreGroups,
} from "@/composables/usePromoCodesQuery";
import SaleCartDetailsModal from "@/components/modals/promo_code/SaleCartDetailsModal.vue";
import HsTable from "@/components/table/HSTable.vue";
import HsPagination from "@/components/table/HSPagination.vue";
import { useStore } from "vuex";

export default {
  name: "GroupPromoUsageTable",
  components: {
    SaleCartDetailsModal,
    HsTable,
    HsPagination,
  },
  props: {
    storeId: {
      type: Number,
      required: true,
    },
    selectedGroupId: {
      type: [String, Number],
      default: "",
    },
    filters: {
      type: Object,
      default: () => ({ startDate: "", endDate: "" }),
    },
  },
  emits: ["groups-loaded", "group-changed"],
  setup(props, { emit }) {
    const store = useStore();
    const localGroupId = ref(props.selectedGroupId || "");
    const internalFilters = ref({
      startDate: "",
      endDate: "",
      page: 1,
      perPage: 10,
    });
    const selectedSaleCart = ref(null);

    // Watch for external group ID changes
    watch(
      () => props.selectedGroupId,
      (newVal) => {
        if (newVal) {
          localGroupId.value = newVal;
        }
      }
    );

    // Watch for external filter changes
    watch(
      () => props.filters,
      (newFilters) => {
        internalFilters.value.startDate = newFilters.startDate || "";
        internalFilters.value.endDate = newFilters.endDate || "";
        internalFilters.value.page = 1;
      },
      { deep: true }
    );

    // Use TanStack Query for groups
    const { data: groupsData, error: groupsError } = useStoreGroups(
      computed(() => props.storeId)
    );
    const groups = computed(() => groupsData.value || []);

    // Watch for groups data and set default group
    watch(
      groups,
      (newGroups) => {
        emit("groups-loaded", newGroups);

        if (
          newGroups.length > 0 &&
          !localGroupId.value &&
          !props.selectedGroupId
        ) {
          // Get current store's default group
          const currentStore = store.getters.getUserStores?.find(
            (s) => s.id === props.storeId
          );
          const defaultGroupId = currentStore?.default_group_id;

          // Select default group if set and exists in list, otherwise first group
          if (
            defaultGroupId &&
            newGroups.some((g) => g.id === defaultGroupId)
          ) {
            localGroupId.value = defaultGroupId;
            emit("group-changed", defaultGroupId);
          } else {
            localGroupId.value = newGroups[0].id;
            emit("group-changed", newGroups[0].id);
          }
        }
      },
      { immediate: true }
    );

    // Watch for groups errors
    watch(groupsError, (newError) => {
      if (newError) {
        // Error is displayed to user via error.value
        toast.error("Failed to load groups");
      }
    });

    // Use TanStack Query for promo usage
    const { data, isLoading, error, refetch } = useGroupPromoUsage(
      localGroupId,
      internalFilters
    );

    // Computed properties from query data
    const items = computed(() => data.value?.items || []);
    const totalItems = computed(() => data.value?.total || 0);
    const totalPages = computed(() => data.value?.pages || 0);
    const currentPage = computed(() => data.value?.page || 1);
    const hasNext = computed(() => data.value?.has_next || false);
    const hasPrev = computed(() => data.value?.has_prev || false);

    // Watch for promo usage errors
    watch(error, (newError) => {
      if (newError) {
        // Error is displayed to user via error.value
        toast.error("Failed to load group promo code usage data");
      }
    });

    const goToPage = (page) => {
      internalFilters.value.page = page;
      refetch();
    };

    const viewDetails = (row) => {
      // Extract raw data from formatted row object
      selectedSaleCart.value = row.__raw || row;
    };
    const handleSaleCartUpdated = ({ response }) => {
      if (!selectedSaleCart.value || !response) return;
      selectedSaleCart.value = {
        ...selectedSaleCart.value,
        ...response,
      };
      refetch();
    };

    const calculateTotal = (paymentPlan) => {
      if (!paymentPlan) return 0;
      const discount = calculateDiscount(paymentPlan);
      return parseFloat(paymentPlan.total_amount_after_discount) + discount;
    };

    const calculateDiscount = (paymentPlan) => {
      if (!paymentPlan?.discount) return 0;
      return parseFloat(paymentPlan.discount.total_discount_amount || 0);
    };

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat("en-RW", {
        style: "currency",
        currency: "RWF",
        minimumFractionDigits: 0,
      }).format(amount);
    };

    const formatDate = (dateString) => {
      if (!dateString) return "N/A";
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    };

    const perPage = computed(() => internalFilters.value.perPage || 10);

    // Table configuration
    const headers = ref([
      "Sale ID",
      "Store",
      "Promo Code",
      "Total Amount",
      "Discount",
      "Date",
      "Actions",
    ]);

    const headerKeyMapping = ref({
      "Sale ID": "saleId",
      Store: "store",
      "Promo Code": "promoCode",
      "Total Amount": "totalAmount",
      Discount: "discount",
      Date: "date",
      Actions: "actions",
    });

    const tableRows = computed(() => {
      return items.value.map((item) => ({
        saleId: item.local_sale_cart_id,
        store: item.store?.name || `Store #${item.store_id}`,
        promoCode: item.payment_plan?.group_promo_code?.code || "N/A",
        totalAmount: formatCurrency(calculateTotal(item.payment_plan)),
        discount: formatCurrency(calculateDiscount(item.payment_plan)),
        date: formatDate(item.created_at),
        actions: "",
        __raw: item,
      }));
    });

    const updatePerPage = (newPerPage) => {
      internalFilters.value.perPage = newPerPage;
      internalFilters.value.page = 1;
      refetch();
    };

    return {
      groups,
      localGroupId,
      selectedSaleCart,
      handleSaleCartUpdated,
      isLoading,
      items,
      totalItems,
      totalPages,
      currentPage,
      hasNext,
      hasPrev,
      perPage,
      headers,
      headerKeyMapping,
      tableRows,
      goToPage,
      updatePerPage,
      viewDetails,
      calculateTotal,
      calculateDiscount,
      formatCurrency,
      formatDate,
    };
  },
};
</script>

<style scoped>
.promo-usage-container {
  @apply w-full;
}

.loading {
  @apply w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin;
}

.btn-secondary {
  @apply bg-gray-200 hover:bg-gray-300 text-gray-700 dark:bg-slate-600 dark:hover:bg-slate-500 dark:text-white;
}
</style>
